
import Vue from "vue";
import { get, pickBy, identity } from "lodash";
import { Confirmation, humanize, initials, timeAgo } from "@/util/helpers";
// import { $Amplify } from "@/main";
import { User } from "@/models/User";
import { useCognitoAdmin } from "@/store/cognitoAdmin";
import { mapState } from "pinia";

interface UserForm {
  username?: string;
  password?: string;
  organisationName?: string;
  group?: string;
  role?: string;
}

type UserFilterField =
  | "username"
  | "email"
  | "organisationName"
  | "role"
  | "group";

export default Vue.extend({
  name: "Users",
  data: () => ({
    shouldOpenRs: true,
    fields: [
      { key: "Username", sortable: true },
      { key: "organisationName", label: "Organisation", sortable: false },
      { key: "groups", label: "Groups", sortable: false },
      { key: "verifiedEmail", label: "Verified Email", sortable: true },
      "Actions",
    ],
    filterOptions: [
      "username",
      "email",
      "organisationName",
      "role",
      "group",
    ] as UserFilterField[],
    filterField: "username" as UserFilterField,
    searchTerm: "",
    hoveredRow: "",
    editingUser: null as null | User,
    createdUser: null as null | User,
    repeatPassword: "",
    userForm: {
      username: "",
      password: "",
      role: "",
      group: "",
      organisationName: "",
    } as UserForm,
    originalUserGroup: "",
  }),
  computed: {
    ...mapState(useCognitoAdmin, [
      "users",
      "organisationsList",
      "groups",
      "currentUser",
    ]),
    filteredUsers(): User[] {
      return this.searchTerm
        ? (useCognitoAdmin().users as User[]).filter(
            (user) =>
              user[this.filterField]
                .toLowerCase()
                .indexOf(this.searchTerm.toLowerCase().trim()) > -1
          )
        : (useCognitoAdmin().users as User[]);
    },
    userEditModalTitle(): string {
      return this.editingUser
        ? "Edit " + this.editingUser.name
        : "Add new user";
    },
    editedUser: {
      get(): UserForm {
        return this.userForm;
      },
      set(user: User | null) {
        this.repeatPassword = "";
        this.userForm.username = user?.username || "";
        this.userForm.password = "";
        this.userForm.role = user?.role || "";
        this.userForm.group = user?.group || "";
        this.userForm.organisationName = user?.organisationName || "";
      },
    },
    isSaveDisabled(): boolean {
      return this.editingUser ? !this.hasMods : !this.validateUser;
    },
    hasMods(): boolean {
      return (
        this.editedUser?.role !== this.editingUser?.role ||
        this.editedUser?.group !== this.editingUser?.group ||
        this.editedUser?.organisationName !== this.editingUser?.organisationName
      );
    },
    validateUser(): boolean {
      // todo: new user form validation
      return true;
    },
  },
  mounted() {
    useCognitoAdmin().listGroupNames();
    useCognitoAdmin().listUsers();
    useCognitoAdmin().getCurrentUser();
  },
  methods: {
    humanize,
    initials,
    timeAgo,
    isRowHovered(user: User) {
      return this.hoveredRow === user.email;
    },
    displayGroup(groups: []): string {
      return groups.length > 0 ? groups.join(", ") : " - ";
    },
    onRowHover(user: User) {
      this.hoveredRow = user.email;
    },
    onRowHoverOut(user: User) {
      if (this.hoveredRow === user.email) this.hoveredRow = "";
    },
    changeFilter(option: UserFilterField) {
      this.filterField = option;
      const searchBox = get(this.$refs, "searchBox.$el");
      this.$nextTick().then(() => {
        if (searchBox instanceof HTMLInputElement) {
          searchBox.focus();
        }
      });
    },
    makeToast(variant: string, message: string, content: string) {
      this.$bvToast.toast(content, {
        title: message,
        variant: variant,
        solid: true,
      });
    },
    resetUserPassword(user: User) {
      Confirmation.fire({
        icon: "question",
        html: "Reset <strong>" + user.name + "</strong>`s password?",
        confirmButtonText: "Reset password",
        confirmButtonAriaLabel: "Confirm password testData",
        cancelButtonAriaLabel: "Decline password testData",
      }).then((r) => {
        if (r.isConfirmed) {
          useCognitoAdmin().resetPassword({ Username: user.username });
        }
      });
    },
    resetForm() {
      this.originalUserGroup = "";
      this.editingUser = null;
      this.createdUser = null;
      this.userForm = {
        username: "",
        organisationName: "",
        group: "",
      };
    },
    alterUserPool(action: string, user: User | null) {
      this.resetForm();
      if (action === "create") {
        const _data = {
          username: "",
          email: "",
          verifiedEmail: "false",
          role: "",
          groups: [],
        };
        this.createdUser = new User(_data);
        this.$bvModal.show("user-edit");
      } else {
        this.originalUserGroup = (user as User).group;
        this.editingUser = user;
        this.editedUser = user;
        this.$bvModal.show("user-edit");
      }
    },
    saveUserEdit() {
      if (this.editingUser) {
        const editValues = pickBy(this.userForm, identity);
        this.updateUserGroup(editValues);

        useCognitoAdmin()
          .updateUserAttributes({
            Username: editValues.username,
            UserAttributes: [
              {
                Name: "custom:organisation",
                Value: editValues.organisationName,
              },
            ],
          })
          .then(() => {
            this.$bvModal.hide("user-edit");
            this.makeToast(
              "success",
              `Successfully updated ${this.editingUser?.username}`,
              "Request completed"
            );
            useCognitoAdmin().listUsers();
            this.editingUser = null;
          })
          .catch(() =>
            this.makeToast(
              "danger",
              `Failed to update ${this.editingUser?.username}`,
              "Request failed"
            )
          );
      } else {
        this.saveCreatedUser();
      }
    },
    async updateUserGroup(editValues: Partial<UserForm>) {
      const newGroup = (editValues as UserForm).group;

      if (newGroup !== "") {
        useCognitoAdmin()
          .addUserToGroup({
            Username: editValues.username,
            GroupName: newGroup,
          })
          .then(() =>
            this.makeToast(
              "success",
              `Added user: ${editValues.username} to group '${newGroup}'`,
              "Request Completed"
            )
          )
          .catch(() =>
            this.makeToast(
              "danger",
              `Failed to add user: ${editValues.username} to group '${newGroup}}'`,
              "Request Failed"
            )
          );
      }
      useCognitoAdmin().removeUserFromGroup({
        Username: editValues.username,
        GroupName: this.originalUserGroup,
      });
    },
    saveCreatedUser() {
      if (this.createdUser) {
        const userAttributes = pickBy(this.userForm, identity);
        const createPayload = {
          Username: userAttributes.username,
          UserAttributes: [
            {
              Name: "custom:organisation",
              Value: userAttributes.organisationName,
            },
            { Name: "email", Value: userAttributes.username },
            { Name: "email_verified", Value: "true" },
          ],
          ValidationData: [{ Name: "email", Value: userAttributes.username }],
        };
        useCognitoAdmin()
          .createUser(createPayload)
          .then(() => {
            this.$bvModal.hide("user-edit");
            this.makeToast(
              "success",
              `Successfully created ${this.createdUser?.username}`,
              "Request completed"
            );
            this.updateUserGroup(userAttributes); // Handle groups
            useCognitoAdmin().listUsers();
            this.editingUser = null;
          })
          .catch(() =>
            this.makeToast(
              "danger",
              `Failed to create ${this.createdUser?.username}`,
              "Request failed"
            )
          );
      }
    },
    deleteUser(user: User) {
      Confirmation.fire({
        icon: "error",
        iconHtml: "!",
        html: "Delete <strong>" + user.name + "</strong>?",
        confirmButtonText: "Delete user",
        confirmButtonAriaLabel: "Confirm user deletion",
        cancelButtonAriaLabel: "Decline user deletion",
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-info mr-3",
        },
        focusConfirm: false,
        focusCancel: true,
      }).then((r) => {
        if (r.isConfirmed) {
          useCognitoAdmin()
            .deleteUser({ Username: user.username })
            .then(() => {
              this.makeToast(
                "success",
                `Successfully deleted ${user.username}.`,
                "Request Completed"
              );
            });
        }
      });
    },
    unlockUser(user: User) {
      Confirmation.fire({
        icon: "question",
        html: "Unlock <strong>" + user.name + "</strong>`s account?",
        confirmButtonText: "Unlock account",
        confirmButtonAriaLabel: "Confirm account unlock",
        cancelButtonAriaLabel: "Cancel account unlock",
      }).then((r) => {
        if (r.isConfirmed) {
          // this.$store.dispatch("admin/unlockUser", user);
        }
      });
    },
  },
});
